import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Tooltip } from '@mui/material'
import { AlertCircle, AlertTriangle, ArrowLeft, CheckCircle2, RefreshCw, X } from 'lucide-react'
import Button from '../../ui/Button'
import Input from '../../ui/Input'
import { showConfirmModal, showErrorModal, showSuccessModal } from '../../ui/ConfirmDialog'
import { UndoToast } from '../UndoToast'
import {
  createCodeIndexAppendix,
  fetchAppendixReferenceTargets,
  fetchCodeIndexAppendix,
  fetchCodeIndexAppendices,
  fetchRevisionAppendix,
  updateCodeIndexAppendix,
} from '../../../services/appendixService'
import type {
  AppendixCodeItemOption,
  AppendixConsistencyResult,
  AppendixDetail,
  AppendixFinding,
  AppendixReferenceTargets,
  AppendixSummary,
  ProposedRule,
  UnselectableRuleLine,
} from '../../../types/codeIndex'
import { useAppendixTableDraft } from '../../../hooks/useAppendixTableDraft'
import {
  AppendixColumnDraft,
  AppendixRowDraft,
  SavedAppendixTableMeta,
  buildAppendixPayload,
  buildRefLabelIndex,
  columnHoldsRefs,
  countUnresolved,
  formatCount,
  formatUnresolvedSummary,
  hydrateAppendixDraft,
  VALID_COMBINATIONS_COLUMN_ERROR,
  findCodeItemOption,
  formatCodeItemTarget,
} from '../../../utils/appendixTable'
import { getApiMessage } from '../../../utils/apiMessage'
import { buildRowsForFinding, getRowFindings, getTableFindings } from '../../../utils/appendixConsistency'
import AppendixColumnsSection from './AppendixColumnsSection'
import AppendixRowsSection from './AppendixRowsSection'
import { AppendixConsistencyBanner, AppendixTableFindings } from './AppendixConsistencySections'
import ColumnTypeChangeDialog from './ColumnTypeChangeDialog'

export const DUPLICATE_APPENDIX_NAME_ERROR = 'An appendix on this codesheet already uses this name.'

const FIELD_LABEL_CLASS = 'mb-1 block text-sm font-semibold text-gray-900'

type AppendixEditorModalProps = {
  open: boolean
  codeIndexId: string
  projectId?: string
  // Opens an existing appendix; without it the modal creates a new one. With `revision` it is the
  // appendix key within that revision.
  appendixId?: string
  // Shows the appendix read-only as it was frozen when this revision was merged into Main.
  revision?: string
  // Changes after each codesheet save so the code items and values on offer are read again. They
  // always come from the saved codesheet, never from unsaved edits on screen.
  codesheetSavedAt?: number | string
  readOnly?: boolean
  saveDisabledReason?: string
  // Revising a rejected pull request change: the save is allowed while the project is submitted
  // and the caller resubmits the change afterwards (so no "saved" toast here).
  revisionChangeId?: string
  revisionPath?: string | null
  revisionChangeType?: string | number
  onClose: () => void
  onSaved: (appendix: AppendixSummary) => void
  // The last comparison of this appendix against the rules (marked appendices only).
  consistency?: AppendixConsistencyResult | null
  consistencyMessage?: string
  // Ways out of a finding that lead to the codesheet; the modal confirms before leaving unsaved edits.
  onEditRule?: (line: UnselectableRuleLine) => void
  onProposeRule?: (proposal: ProposedRule) => void
  onGoToCodeItem?: (code: string) => void
  // Bumped by the caller after a save to reload the appendix in place (fresh row ids for the
  // comparison against the rules) without remounting the dialog.
  refreshKey?: number
}

type LoadState = 'idle' | 'loading' | 'error' | 'ready'

function serializeDraft(name: string, columns: AppendixColumnDraft[], rows: AppendixRowDraft[], definesValidCombinations = false): string {
  return JSON.stringify([name.trim(), columns, rows, definesValidCombinations])
}

function formatAppendixTitle(detail: AppendixDetail): string {
  return detail.letter ? `${detail.letter} — ${detail.title}` : detail.title
}

export const AppendixEditorModal: React.FC<AppendixEditorModalProps> = ({
  open,
  codeIndexId,
  projectId,
  appendixId,
  revision,
  codesheetSavedAt,
  readOnly: readOnlyProp = false,
  saveDisabledReason,
  revisionChangeId,
  revisionPath,
  revisionChangeType,
  onClose,
  onSaved,
  consistency,
  consistencyMessage,
  onEditRule,
  onProposeRule,
  onGoToCodeItem,
  refreshKey = 0,
}) => {
  const readOnly = readOnlyProp || Boolean(revision)
  const [targets, setTargets] = useState<AppendixReferenceTargets | null>(null)
  const [targetsError, setTargetsError] = useState<string | null>(null)
  const [definesValidCombinations, setDefinesValidCombinations] = useState(false)
  // Set once a save was attempted, so the valid combinations rule shows only after that.
  const [saveAttempted, setSaveAttempted] = useState(false)
  const validityRevision = Boolean(revisionChangeId && revisionPath === 'defines_valid_combinations')
  const lockValidity = Boolean(revisionChangeId && !validityRevision && String(revisionChangeType).toLowerCase() !== 'create' && revisionChangeType !== 0)
  const table = useAppendixTableDraft(targets, definesValidCombinations)
  const { columns, rows, reset } = table
  const isEditMode = Boolean(appendixId)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [columnsExpanded, setColumnsExpanded] = useState(true)
  const [autoFocusColumnKey, setAutoFocusColumnKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadState, setLoadState] = useState<LoadState>('idle')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [detail, setDetail] = useState<AppendixDetail | null>(null)
  const [savedMeta, setSavedMeta] = useState<SavedAppendixTableMeta | undefined>(undefined)
  const [baseline, setBaseline] = useState<string | null>(null)
  const nameCheckRef = useRef(0)
  const loadRequestRef = useRef(0)
  const targetsRequestRef = useRef(0)
  const refIndex = useMemo(() => buildRefLabelIndex(targets, detail), [detail, targets])

  const loadTargets = useCallback(async () => {
    if (!codeIndexId) return

    const requestId = targetsRequestRef.current + 1
    targetsRequestRef.current = requestId
    setTargetsError(null)
    try {
      const loaded = await fetchAppendixReferenceTargets(codeIndexId, { projectId })
      if (targetsRequestRef.current !== requestId) return
      setTargets(loaded)
    } catch (err) {
      if (targetsRequestRef.current !== requestId) return
      console.error('Failed to load the code items an appendix can reference.', err)
      setTargetsError(getApiMessage(err) || 'Unable to load the code items.')
    }
  }, [codeIndexId, projectId])

  // The id this modal just created: the caller then reopens it in edit mode, which must not wipe
  // the editor and flash the loading state.
  const createdIdRef = useRef<string | null>(null)
  const refreshKeyRef = useRef(refreshKey)

  // A silent load keeps the current content on screen until the fresh copy arrives.
  const loadAppendix = useCallback(async (silent = false) => {
    if (!appendixId) return

    const requestId = loadRequestRef.current + 1
    loadRequestRef.current = requestId
    if (!silent) setLoadState('loading')
    setLoadError(null)
    try {
      const loaded = revision
        ? await fetchRevisionAppendix(codeIndexId, revision, appendixId)
        : await fetchCodeIndexAppendix(codeIndexId, appendixId, { projectId })
      if (loadRequestRef.current !== requestId) return

      const hydrated = hydrateAppendixDraft(loaded)
      reset({ columns: hydrated.columns, rows: hydrated.rows })
      setDetail(loaded)
      setSavedMeta(hydrated.meta)
      setName(loaded.title)
      setDefinesValidCombinations(loaded.definesValidCombinations ?? false)
      setBaseline(serializeDraft(loaded.title, hydrated.columns, hydrated.rows, loaded.definesValidCombinations ?? false))
      if (!silent) setColumnsExpanded(hydrated.columns.length === 0)
      setLoadState('ready')
    } catch (err) {
      if (loadRequestRef.current !== requestId) return
      console.error('Failed to load appendix.', err)
      setLoadError(getApiMessage(err) || 'Unable to load the appendix.')
      setLoadState('error')
    }
  }, [appendixId, codeIndexId, projectId, reset, revision])

  useEffect(() => {
    loadRequestRef.current += 1
    targetsRequestRef.current += 1
    nameCheckRef.current += 1
    if (!open) {
      createdIdRef.current = null
      return
    }

    if (appendixId && appendixId === createdIdRef.current) {
      createdIdRef.current = null
      void loadAppendix(true)
      return
    }

    reset()
    setName('')
    setDefinesValidCombinations(false)
    setSaveAttempted(false)
    setNameError(null)
    setColumnsExpanded(true)
    setAutoFocusColumnKey(null)
    setSaving(false)
    setDetail(null)
    setSavedMeta(undefined)
    setBaseline(null)
    setLoadError(null)
    setLoadState('idle')
    setTargets(null)
    setTargetsError(null)
    if (appendixId) void loadAppendix()
  }, [appendixId, loadAppendix, open, reset])

  useEffect(() => {
    if (refreshKeyRef.current === refreshKey) return
    refreshKeyRef.current = refreshKey
    if (open && appendixId) void loadAppendix(true)
  }, [appendixId, loadAppendix, open, refreshKey])

  // Read on open and again after every codesheet save; a frozen revision resolves on the server.
  useEffect(() => {
    if (!open || revision) return
    void loadTargets()
  }, [codesheetSavedAt, loadTargets, open, revision])

  const targetsReady = Boolean(revision) || targets !== null
  const isLoaded = !isEditMode || (loadState === 'ready' && targetsReady)
  const trimmedName = name.trim()
  const hasTableContent = Boolean(trimmedName && columns.length > 0 && rows.length > 0)
  const isDirty = readOnly || !isLoaded
    ? false
    : isEditMode
      ? serializeDraft(name, columns, rows, definesValidCombinations) !== baseline
      : Boolean(trimmedName || columns.length > 0 || rows.length > 0 || definesValidCombinations)
  const canSave = isLoaded && hasTableContent && !nameError && !saving && !readOnly && !saveDisabledReason
  const inputsDisabled = saving || readOnly || validityRevision
  const referenced = columns
    .filter(column => column.dataType === 'CodeItem')
    .map(column => findCodeItemOption(table.codeItems, column))
    .filter((target): target is AppendixCodeItemOption => Boolean(target && !target.positions?.length))
  const hasReference = referenced.length > 0
  // Mirrors the server: a table that defines valid combinations needs a code item column.
  const validityError = definesValidCombinations && !columns.some(column => column.dataType === 'CodeItem')
    ? VALID_COMBINATIONS_COLUMN_ERROR
    : null
  const scope = Array.from(new Set(referenced.map(target => formatCodeItemTarget(target)))).join(', ')

  const baseTitle = isEditMode ? (detail ? formatAppendixTitle(detail) : 'Appendix') : 'New appendix'
  const title = revision ? `${baseTitle} (Rev ${revision})` : baseTitle
  // A frozen revision keeps the count resolved when it was merged; a live appendix counts the draft.
  const unresolvedCount = revision ? detail?.unresolvedCount ?? 0 : countUnresolved(columns, rows, refIndex)
  const showCompleteness = isEditMode && isLoaded && Boolean(detail)
  const footerHint = !isLoaded
    ? ''
    : !hasTableContent
      ? 'Name, a column and a row are needed'
      : `${formatCount(columns.length, 'column')} - ${formatCount(rows.length, 'row')}`

  const handleClose = async () => {
    if (saving) return
    if (isDirty) {
      const confirmed = await showConfirmModal({
        title: isEditMode ? 'Discard changes?' : 'Discard appendix?',
        description: isEditMode ? 'Your changes to this appendix will not be saved.' : 'The appendix you started will not be saved.',
        confirmText: 'Discard',
        cancelText: 'Keep editing',
      })
      if (!confirmed) return
    }
    onClose()
  }

  const handleNameChange = (value: string) => {
    setName(value)
    setNameError(null)
    nameCheckRef.current += 1
  }

  const checkNameAvailability = async () => {
    if (!trimmedName || !codeIndexId || readOnly) return

    const requestId = nameCheckRef.current + 1
    nameCheckRef.current = requestId
    try {
      const matches = await fetchCodeIndexAppendices(codeIndexId, { projectId, search: trimmedName })
      if (nameCheckRef.current !== requestId) return
      const normalizedName = trimmedName.toLowerCase()
      const isTaken = matches.some(appendix =>
        appendix.id !== appendixId && appendix.title.trim().toLowerCase() === normalizedName
      )
      setNameError(isTaken ? DUPLICATE_APPENDIX_NAME_ERROR : null)
    } catch (err) {
      // The server checks the name again on save.
      console.error('Failed to check the appendix name.', err)
    }
  }

  // A finding's way out that leads to the codesheet closes the appendix; unsaved edits are confirmed first.
  const leaveForCodesheet = async (action: () => void) => {
    if (isDirty) {
      const confirmed = await showConfirmModal({
        title: 'Leave the appendix?',
        description: 'Your unsaved changes to this appendix will be lost.',
        confirmText: 'Leave',
        cancelText: 'Keep editing',
      })
      if (!confirmed) return
    }
    action()
  }

  const handleAddFindingToTable = (finding: AppendixFinding) => {
    const added = buildRowsForFinding(finding, columns)
    if (added) {
      // The finding names values by code; a code item cell holds the key of the value.
      table.appendRows(added.map(cells => Object.fromEntries(Object.entries(cells).map(([columnKey, code]) => {
        const column = columns.find(candidate => candidate.key === columnKey)
        if (!column || !columnHoldsRefs(column)) return [columnKey, code]
        const option = findCodeItemOption(table.codeItems, column)
        return [columnKey, option?.values.find(value => value.code === code)?.key ?? '']
      }))))
      return
    }
    void showErrorModal({
      title: 'Columns have changed',
      description: 'A column this finding covers was removed or replaced since the last save. Save the appendix to recheck it.',
      confirmText: 'OK',
    })
  }

  const showConsistency = Boolean(consistency) && definesValidCombinations && isEditMode
  const rowFindings = showConsistency ? getRowFindings(consistency) : undefined
  const tableFindings = showConsistency ? getTableFindings(consistency) : []

  const handleAddColumn = () => {
    const columnKey = table.addColumn()
    setAutoFocusColumnKey(columnKey)
    setColumnsExpanded(true)
  }

  const handleSave = async () => {
    if (!canSave) return

    setSaveAttempted(true)
    const { columnsValid, cellsValid } = table.validateAll()
    if (!columnsValid) setColumnsExpanded(true)
    if (!columnsValid || !cellsValid || validityError) return

    setSaving(true)
    try {
      const payload = buildAppendixPayload(trimmedName, columns, rows, isEditMode ? savedMeta : undefined, definesValidCombinations)
      const summary = appendixId
        ? await updateCodeIndexAppendix(
            codeIndexId,
            appendixId,
            payload,
            revisionChangeId ? { projectId, changeId: revisionChangeId } : { projectId }
          )
        : await createCodeIndexAppendix(codeIndexId, payload, { projectId })
      if (!appendixId) createdIdRef.current = summary.id
      onSaved(summary)
      if (!revisionChangeId) {
        await showSuccessModal({
          title: isEditMode ? 'Appendix saved' : 'Appendix Created',
          timer: 1200,
          showConfirmButton: false,
        })
      }
    } catch (err) {
      const message = getApiMessage(err)
      if (message === DUPLICATE_APPENDIX_NAME_ERROR) {
        setNameError(message)
        return
      }

      console.error('Failed to save appendix', err)
      await showErrorModal({
        title: 'Error',
        description: message || (isEditMode ? 'Failed to save appendix. Please try again.' : 'Failed to create appendix. Please try again.'),
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const retryLoad = () => {
    if (isEditMode && loadState === 'error') void loadAppendix()
    if (targetsError) void loadTargets()
  }

  const renderBody = () => {
    const errorMessage = (isEditMode && loadState === 'error' ? loadError : null) || targetsError
    if (isEditMode && !errorMessage && (loadState === 'loading' || loadState === 'idle' || !targetsReady)) {
      return (
        <div className="flex min-h-[240px] items-center justify-center" role="status" aria-label="Loading appendix">
          <CircularProgress size={24} />
        </div>
      )
    }

    if (errorMessage) {
      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 px-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-red-700">Unable to load appendix</p>
            <p className="text-sm text-red-600">{errorMessage}</p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={retryLoad}
            leftIcon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
          >
            Retry
          </Button>
        </div>
      )
    }

    return (
      <div className="space-y-5">
        {definesValidCombinations && consistencyMessage ? (
          <div role="status" className="text-sm text-[#1D5176]">{consistencyMessage}</div>
        ) : null}
        {showConsistency && consistency ? (
          <div className="-mx-5 -mt-5">
            <AppendixConsistencyBanner result={consistency} needsRecheck={isDirty} />
          </div>
        ) : null}
        <div>
          <label htmlFor="appendix-editor-name" className={FIELD_LABEL_CLASS}>Name</label>
          {nameError ? (
            <p role="alert" className="mb-1 flex items-center gap-1.5 text-xs text-[#B3261E]">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              {nameError}
            </p>
          ) : null}
          <Input
            id="appendix-editor-name"
            inputSize="md"
            value={name}
            invalid={Boolean(nameError)}
            placeholder="Give this appendix a name"
            onChange={(event) => handleNameChange(event.target.value)}
            onBlur={() => void checkNameAvailability()}
            disabled={inputsDisabled}
            className={`max-w-[360px]${nameError ? ' !bg-[#FFF5F5]' : ''}`}
          />
        </div>

        <div className="rounded-lg border border-[#E2E8F0] p-3">
          <label className="flex min-h-8 items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" className="h-4 w-4 flex-shrink-0 accent-[#006B9A]"
              checked={definesValidCombinations}
              disabled={saving || readOnly || lockValidity || (!hasReference && !definesValidCombinations)}
              onChange={event => setDefinesValidCombinations(event.target.checked)} />
            This table defines valid combinations
          </label>
          <p className="ml-6 text-xs text-gray-500">
            {!hasReference ? 'Point a column at a code item first.' : `Scope: ${scope}. Defines valid configurations for comparison with rules; availability rules still govern selection.`}
          </p>
          {saveAttempted && validityError ? (
            <p role="alert" className="ml-6 mt-1 flex items-center gap-1.5 text-xs text-[#B3261E]">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              {validityError}
            </p>
          ) : null}
        </div>

        <AppendixColumnsSection
          columns={columns}
          codeItems={table.codeItems}
          refIndex={refIndex}
          columnErrors={table.columnErrors}
          firstColumnError={table.firstColumnError}
          expanded={columnsExpanded}
          autoFocusColumnKey={autoFocusColumnKey}
          disabled={saving}
          readOnly={readOnly || validityRevision}
          onToggleExpanded={() => setColumnsExpanded(current => !current)}
          onAddColumn={handleAddColumn}
          onUpdateColumn={table.updateColumn}
          definesValidCombinations={definesValidCombinations}
          onRequestTypeChange={table.requestColumnTypeChange}
          onDeleteColumn={table.deleteColumn}
          onMoveColumn={table.moveColumn}
        />

        <AppendixRowsSection
          columns={columns}
          rows={rows}
          codeItems={table.codeItems}
          refIndex={refIndex}
          cellErrors={table.cellErrors}
          firstCellError={table.firstCellError}
          disabled={saving}
          readOnly={readOnly || validityRevision}
          onAddRow={table.addRow}
          onDeleteRow={table.deleteRow}
          onUpdateCell={table.updateCell}
          onValidateCell={table.validateCellAt}
          onClearCell={table.clearCell}
          rowFindings={rowFindings}
          onEditRule={onEditRule ? (line) => void leaveForCodesheet(() => onEditRule(line)) : undefined}
        />

        <AppendixTableFindings
          findings={tableFindings}
          disabled={saving}
          readOnly={readOnly}
          onAddToTable={handleAddFindingToTable}
          onProposeRule={onProposeRule
            ? (finding) => {
                if (finding.proposal) void leaveForCodesheet(() => onProposeRule(finding.proposal as ProposedRule))
              }
            : undefined}
          onGoToCodeItem={onGoToCodeItem ? (code) => void leaveForCodesheet(() => onGoToCodeItem(code)) : undefined}
        />
      </div>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={() => void handleClose()}
      maxWidth={false}
      disableEnforceFocus
      aria-labelledby="appendix-editor-title"
      PaperProps={{ sx: { borderRadius: '8px', width: 780, maxWidth: 'calc(100% - 32px)' } }}
    >
      <div className="flex items-center gap-2 border-b border-[#E2E8F0] px-4 py-3">
        <IconButton size="small" onClick={() => void handleClose()} aria-label="Back to appendices">
          <ArrowLeft className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
        </IconButton>
        <h2 id="appendix-editor-title" className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-900">
          {title}
        </h2>
        <IconButton size="small" onClick={() => void handleClose()} aria-label="Close">
          <X className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: '20px' }}>
        {renderBody()}
      </DialogContent>

      <DialogActions sx={{ padding: '12px 20px', borderTop: '1px solid #E2E8F0', justifyContent: 'space-between' }}>
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-sm text-gray-500">{footerHint}</span>
          {showCompleteness ? (
            unresolvedCount === 0 ? (
              <span data-testid="appendix-completeness" className="inline-flex items-center gap-1 rounded-full bg-[#E7F5EC] px-2 py-0.5 text-xs font-medium text-[#1F7A3F]">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Complete
              </span>
            ) : (
              <span data-testid="appendix-completeness" className="inline-flex items-center gap-1 text-xs font-medium text-[#B3261E]">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                {formatUnresolvedSummary(unresolvedCount)}
              </span>
            )
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" size="sm" className="bg-[#EEF6FF]" onClick={() => void handleClose()} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly ? (
            <Tooltip title={saveDisabledReason || ''}>
              <span>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void handleSave()}
                  disabled={!canSave}
                  leftIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : undefined}
                >
                  {revisionChangeId ? 'Save & Resubmit' : 'Save'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </div>
      </DialogActions>

      {!readOnly ? (
        <ColumnTypeChangeDialog
          change={table.pendingTypeChange}
          onKeep={table.cancelTypeChange}
          onConfirm={table.confirmTypeChange}
        />
      ) : null}

      {table.undoToast ? (
        <UndoToast
          key={table.undoToast.id}
          variant="warning"
          position="bottom-right"
          title={table.undoToast.title}
          message={table.undoToast.message}
          timestampLabel="just now"
          onUndo={table.undo}
          onClose={table.dismissUndo}
        />
      ) : null}
    </Dialog>
  )
}

export default AppendixEditorModal
